import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

/**
 * Every asset actually attached to an application, deduplicated — assets can
 * be linked at the program-instance, course, application, or assistance
 * (payment) level, and the same asset can legitimately appear via more than
 * one of those relationships. Mirrors approval-page.gts's own
 * `allAttachments` aggregation.
 *
 * Deliberately unfiltered (deleted assets included): the Attachments drawer
 * strikes deleted rows through rather than hiding them, and CategorizedUpload's
 * prior-uploads block applies its own live-assets filter (#10393).
 *
 * Shared by ManagePage (the Attachments drawer) and the upload task cards
 * (CategorizedUpload's `@relatedAssets`, which feeds its "Previously
 * uploaded" history) so the two surfaces can never disagree about what is
 * attached.
 */
export function collectApplicationAssets(application: TasApplicationModel): TasAssetModel[] {
  const programInstanceAssets = application.tasProgramInstance?.tasAssets ?? [];
  const courseAssets = application.activeCourses?.flatMap((course) => course.tasAssets ?? []) ?? [];
  const appAssets = application.tasAssets ?? [];
  const assistanceAssets =
    application.tasAssistances?.flatMap((assistance) => assistance.tasAssets ?? []) ?? [];

  const seenIds = new Set<string>();
  const allAssets = [...programInstanceAssets, ...courseAssets, ...appAssets, ...assistanceAssets];
  return allAssets.filter((asset) => {
    const id = asset.id;
    if (!id || seenIds.has(id)) {
      return false;
    }
    seenIds.add(id);
    return true;
  });
}
